import React, { FC } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  InputAdornment,
  SvgIcon,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { MdSearch as SearchIcon } from 'react-icons/md';
import { RepoNode } from '../../onboarding-types';
import RepoSelectionTree from './RepoSelectionTree';
import { MdErrorOutline as ErrorOutline } from 'react-icons/md';
import { important } from 'common-types';
import { LoadingButton } from '@mui/lab';
import RepoSelectionStore from '../../stores/onboarding-repo-selection-store';
import { useSnapshot } from 'valtio';

const OnboardingAppSelection: FC<
  React.PropsWithChildren<OnboardingAppSelectionProps>
> = ({
  reposNodes,
  reposFound,
  chooseAllSelected,
  onSelectChooseAll,
  handleSearchRepos,
  searchValue,
  onContinueClick,
  isLoading,
  disableContinue,
  handleMonitorNewResources,
  monitorNewResources,
  submitButtonText,
  checkedReposNumber,
  reposAmountAllowed,
}) => {
  const { controlObject } = useSnapshot(RepoSelectionStore);
  const maxReposAllowed =
    reposAmountAllowed > reposFound ? reposFound : reposAmountAllowed;

  const maxReposExceeded = checkedReposNumber > maxReposAllowed;

  const { classes } = useStyles();
  return (
    <>
      <div className={classes.header}>
        <Typography variant='h6' color='text.primary'>
          <p>{reposFound} repos found</p>
          <p>Please choose repos to monitor and protect:</p>
        </Typography>
      </div>
      <Divider className={classes.divider} />
      <div className={classes.topSection}>
        <FormControlLabel
          control={
            <Checkbox
              checked={chooseAllSelected}
              onChange={onSelectChooseAll}
            />
          }
          label='Choose all'
        />
        <div>
          <TextField
            fullWidth
            size='small'
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SvgIcon fontSize='small' color='action'>
                    <SearchIcon />
                  </SvgIcon>
                </InputAdornment>
              ),
            }}
            placeholder='Search'
            variant='outlined'
            onChange={handleSearchRepos}
            value={searchValue}
          />
        </div>
      </div>
      <Divider className={classes.divider} />
      <div className={classes.header}>
        <Typography
          variant='caption'
          color={maxReposExceeded ? 'error.main' : 'text.primary'}>
          {checkedReposNumber}/{maxReposAllowed} selected{' '}
          {maxReposExceeded &&
            `- your membership tier is limited to ${maxReposAllowed} repositories`}
        </Typography>
      </div>
      <div className={classes.appSelectionTreeContainer}>
        {reposNodes.length > 0 && (
          <RepoSelectionTree
            treeData={reposNodes}
            controlObject={controlObject}
          />
        )}
        {reposNodes.length === 0 && (
          <Typography
            variant='body1'
            fontWeight='bold'
            color='text.primary'
            justifyContent='center'
            m={2}
            display='flex'
            alignItems='center'>
            <ErrorOutline className={classes.noReposFoundIcon} />
            No repos found
          </Typography>
        )}
      </div>
      <Divider className={classes.divider} />
      <div className={classes.footerCheckboxContainer}>
        <FormControlLabel
          control={
            <Checkbox
              checked={monitorNewResources}
              onChange={handleMonitorNewResources}
            />
          }
          label='Monitor all newly created repos'
        />
      </div>
      <div className={classes.footer}>
        {maxReposExceeded && (
          <Typography variant='caption' color='error.main'>
            Your membership tier is limited to a scan of {maxReposAllowed}{' '}
            repositories.
          </Typography>
        )}
        <LoadingButton
          disabled={isLoading || disableContinue || maxReposExceeded}
          loading={isLoading}
          className={classes.connectButton}
          onClick={onContinueClick}
          variant='contained'
          color='primary'>
          {submitButtonText || 'Continue'}
        </LoadingButton>
      </div>
    </>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  header: {
    textAlign: 'start',
    '& p': {
      margin: 0,
    },
  },
  divider: {
    marginBlock: important(theme.spacing(1.5)),
  },
  topSection: {
    display: 'flex',
    margin: theme.spacing(1.5, 0),
    justifyContent: 'space-between',
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  connectButton: {
    borderRadius: important('50px'),
    marginBlockStart: important(theme.spacing(1)),
    minWidth: important('170px'),
  },
  appSelectionTreeContainer: {
    textAlign: 'start',
    maxHeight: 600,
    minHeight: 200,
  },
  noReposFoundIcon: {
    marginInlineEnd: theme.spacing(1),
  },
  footerCheckboxContainer: {
    textAlign: 'start',
  },
}));

interface OnboardingAppSelectionProps {
  reposNodes: RepoNode[];
  reposFound: number;
  chooseAllSelected: boolean;
  onSelectChooseAll: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearchRepos: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onContinueClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  searchValue: string;
  isLoading: boolean;
  disableContinue: boolean;
  monitorNewResources: boolean;
  handleMonitorNewResources: (e: React.ChangeEvent<HTMLInputElement>) => void;
  submitButtonText?: string;
  checkedReposNumber: number;
  reposAmountAllowed: number;
}

export default OnboardingAppSelection;
